import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, set } from '@ember/object';
import { camelize, dasherize } from '@ember/string';
import { next, later } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { dependentKeyCompat } from '@ember/object/compat';

import SpecificationsAdvisor from '../models/advisors/specification';
import ComponentsAdvisor from '../models/advisors/component-selection';
import TolerancesAdvisor from '../models/advisors/tolerance';
import NoiseAdvisor from '../models/advisors/noise';
import FigureMap from '../figure-map';
import FilterConstants from '../filter-constants';
import {
  sendTabExitAnalytics,
  sendTabNavigationAnalytics,
  sendPartStageDataNextStepsAnalytics,
  sendLocaleChangeAnalytics,
  shareDesignAnalytics,
  sendOpenCloseDialogAnalytics,
  downloadDesignAnalytics,
  toolbarLinkClickAnalytics,
} from '../utils/analytics';
import { downloadFile } from '../utils/download';
import DesignFiles from '../utils/design-files';
import RSVP from 'rsvp';
import ENV from '../config/environment';
import { getURL } from '../utils/resource';
import * as SpiceNetlistsUtil from '../utils/spice-netlists';
import * as CommonUtils from '../utils/common';
import * as SpiceSchematicsUtil from '../utils/spice-schematics';

///////////////////////////////////////////////////////////////////////////////
// Index Controller: Controller for the main route of the application,       //
// controlling the flow of information between the UI and the Index Model.   //
///////////////////////////////////////////////////////////////////////////////

// Map tab names to corresponding input and result members of the model.
// TODO: Convention over configuration with a camelize function would help here.
let tabList = ['filter-type', 'specifications', 'components', 'tolerances', 'next-steps'];
let tabIcons = {
  'filter-type': 'hf-response',
  specifications: 'step-plot',
  components: 'block-diagram',
  tolerances: 'step-plot',
  'next-steps': 'timing',
};
let inputModels = {
  'filter-type': 'filterType', // 'model.inputs.filterType',
  specifications: 'specifications', // 'model.inputs.specifications',
  components: 'components', // 'model.inputs.components',
  tolerances: 'tolerances', // 'model.inputs.tolerances',
  'next-steps': 'nextSteps', // 'model.inputs.nextSteps',
  noise: 'noise', // 'model.inputs.noise',
};
let inputModelReleaseOrder = ['noise', 'next-steps', 'tolerances', 'components', 'specifications', 'filter-type'];
let defaultFigures = {
  'filter-type': '',
  specifications: 'magnitudeDb',
  components: 'circuit',
  tolerances: 'circuit',
  'next-steps': 'circuit',
};
let figuresOfTab = FigureMap;

const videoIds = [
  'analog_digital',
  'active_passive',
  'chebyshev_butterworth_bessel',
  'quality_factor',
  'active_filter',
  'sallenkey_feedback',
  'gain_attenuation',
  'opamp_output_impedance',
  'picking_components',
];

let delay = function (t, v) {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(null, v), t);
  });
};

export default class IndexController extends Controller {
  @service logger;
  @service intl;
  @service('aditools-site') siteconfig; // Used by videos
  @service persistence;
  @service('aditools-analytics') analytics;
  @service share;
  @service download;
  @service cdp;
  @service fastboot;
  @service feedback;
  @service opamps;
  @service channel;
  @service assets;
  @service snapshot;

  @tracked tool = null;
  @tracked width = null;
  @tracked visitedTabs = [];
  @tracked activeTab = null;
  @tracked selectedFigure = null;
  @tracked fromErrors = [];
  @tracked fromModel = [];
  @tracked videoIds = videoIds;

  queryParams = ['lang', 'pdf', 'tool', 'design', 'invokerId'];
  pdf = 'remote';
  lang = null;
  design = null;
  invokerId = null;
  advisors = null;
  tabs = null;
  errors = null;
  tabIcons = null;
  notificationDialog = null;
  helpDialog = null;
  systemDialog = null;
  dialogFlags = null;
  edgeWarning = null;
  designFiles = null;
  designName = null;
  designDesc = null;
  pdfFigures = null;
  evalBoardDisplayData = null;
  evalBoardMultiple = null;
  shoppingCartBaseUrl = null;
  shoppingCartEvalBoardUrl = null;
  stackedTabs = null;
  height = null;
  navElement = null;
  boundResizeListener = null;
  htmlToCanvas = null;
  pdfReport = null;
  systemLock = null;
  handShakeTimeOut = null;
  designSendTimeOut = null;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  constructor(...args) {
    super(...args);
    this.tabs = tabList;
    this.activeTab = this.tabs[0];
    set(this, 'errors', []);
    set(this, 'advisors', {});
    set(this, 'tabIcons', tabIcons);
    set(this, 'notificationDialog', { title: '', message: '', enabled: false });
    set(this, 'helpDialog', { title: '', message: '', enabled: false });
    set(this, 'edgeWarning', { title: '', message: '', enabled: false });
    set(this, 'systemDialog', { message: '', enabled: false });
    set(this, 'dialogFlags', {
      orderSamplesEnabled: false,
      evalBoardEnabled: false,
      mailToColleagueEnabled: false,
      evalBoardConfirmationEnabled: false,
      algorithmEnabled: false,
    });
    set(
      this,
      'designFiles',
      new DesignFiles(this.cdp, this.persistence, this.logger, this.intl, this.fastboot, this.assets),
    );
    set(this, 'pdfFigures', { render: false, components: [] });
    set(this, 'stackedTabs', false);

    this.fromErrors = this._updateFromErrors('');
    this.fromModel = this._updateFromModel();

    set(this, 'figurePerTab', Object.assign({}, defaultFigures));
    set(this, 'systemLock', { title: '', message: '', enabled: false });
  }

  setupStudioLinks() {
    this.precisionStudioMenuLinks = {
      PrecisionStudioUrl: this.siteconfig.PrecisionStudioUrl,
      FilterWizardUrl: this.siteconfig.FilterWizardUrl,
      AdcDriverUrl: this.siteconfig.AdcDriverUrl,
      PhotodiodeUrl: this.siteconfig.PhotodiodeUrl,
      DiamondUrl: this.siteconfig.DiamondUrl,
      SimDDSUrl: this.siteconfig.SimDDSUrl,
      VirtualEvalUrl: this.siteconfig.VirtualEvalUrl,
      DACErrorCalculatorUrl: this.siteconfig.DACErrorCalculatorUrl,
      NoiseUrl: this.siteconfig.NoiseUrl,
      SignalChainDesignerUrl: this.siteconfig.SignalChainDesignerUrl,
    };
  }

  setupShoppingCartUrl() {
    this.logger.time('shoppingCartTime');
    if (!this.isDestroyed) {
      set(this, 'shoppingCartBaseUrl', this.siteconfig.ShoppingCartBaseUrl);
      set(
        this,
        'shoppingCartEvalBoardUrl',
        this.siteconfig.ShoppingCartEvalBoardUrl.replace('{cdpLocale}', this.siteconfig.CdpLocale),
      );
    }
    this.logger.timeEnd('shoppingCartTime');
  }

  setupAdvisors() {
    const model = this.model;
    set(this.advisors, 'specifications', new SpecificationsAdvisor(model));
    set(this.advisors, 'components', new ComponentsAdvisor(model));
    set(this.advisors, 'tolerances', new TolerancesAdvisor(model));
    set(this.advisors, 'noise', new NoiseAdvisor(model));
  }

  checkBrowserSuitability() {
    set(this, 'edgeWarning', {
      title: this.intl.t('help.to-precision-warning.title'),
      message: this.intl.t('help.to-precision-warning.message'),
      enabled: this.isToPrecisionUnreliable(),
    });
  }

  isToPrecisionUnreliable() {
    return (2e-6).toPrecision(3) !== '0.00000200';
  }

  setupConnectionToChannel() {
    if (this.invokerId) {
      return RSVP.Promise.resolve()
        .then(() => {
          this.systemDialogFunc('ON', 'connecting-to', { tool: this.toolNameDisplay });
          return delay(1000).then(() => {
            this.channel.setInvokerId(this.invokerId);
            return;
          });
        })
        .then(() => {
          let eventList = {
            onConnect: this.onConnectHandler,
            onDisconnect: this.onDisconnectHandler,
            onData: this.onDataHandler,
          };
          set(this, 'handShakeTimeOut', { timeOut: null, handlerFunc: this.handShakeTimeOutError });
          this.logger.log('Setup Integration Connection');
          return this.channel.connect(eventList, this.handShakeTimeOut);
        })
        .catch((error) => {
          this.systemDialogFunc('OFF', '');
          set(this.systemLock, 'enabled', true);
          set(this.systemLock, 'title', this.intl.t('dialogs.connect-to.title-error'));
          set(
            this.systemLock,
            'message',
            this.intl.t('dialogs.connect-to.message-error', { tool: this.toolNameDisplay }),
          );
          this.logger.log('Error:' + error.toString());
        });
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Computed Properties - used from handlebars                                 //
  ////////////////////////////////////////////////////////////////////////////////

  // Enabled tabs always include visited tabs + the next
  // Filter type tab is not considered visited until a filter type is selected.
  @dependentKeyCompat
  get enabledTabs() {
    let visitedTabs = this.visitedTabs.slice(),
      tabs = this.tabs,
      fromErrors = this.fromErrors,
      fromModel = this.fromModel,
      result = [];
    for (let i = 1, iLen = tabs.length; i < iLen; i++) {
      if (!visitedTabs.includes(tabs[i]) && visitedTabs.includes(tabs[i - 1])) {
        visitedTabs.push(tabs[i]);
        break;
      }
    }
    if (visitedTabs.length === 0) {
      visitedTabs.push('filter-type');
    }
    for (let i = 0; i < visitedTabs.length; i++) {
      if (fromErrors.includes(visitedTabs[i]) && fromModel.includes(visitedTabs[i])) {
        result.push(visitedTabs[i]);
      }
    }
    return result;
  }

  get isSpecificationsDisabled() {
    return !this.enabledTabs.includes('specifications');
  }

  get isComponentsDisabled() {
    return !this.enabledTabs.includes('components');
  }

  get isTolerancesDisabled() {
    return !this.enabledTabs.includes('tolerances');
  }

  get isNextStepssDisabled() {
    return !this.enabledTabs.includes('next-steps');
  }

  @dependentKeyCompat
  get activeTabFieldName() {
    return camelize(this.activeTab);
  }

  @dependentKeyCompat
  get currentTabFigures() {
    return figuresOfTab[this.activeTab];
  }

  @dependentKeyCompat
  get figure() {
    let tab = this.activeTab,
      selectedName = this.selectedFigure,
      figureItem = figuresOfTab[tab].find(function (item) {
        return item.name === selectedName;
      });
    return figureItem;
  }

  @dependentKeyCompat
  get isWideDisplay() {
    let result = this.width >= 950 ? true : false;
    return result;
  }

  @dependentKeyCompat
  get isNarrowDisplay() {
    let result = this.width < 950 ? true : false;
    return result;
  }

  @dependentKeyCompat
  get toolNameDisplay() {
    return this.tool ? decodeURIComponent(this.tool) : null;
  }

  @dependentKeyCompat
  get orderEvalboardDisplay() {
    return 'display';
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Internal Support Functions                                                 //
  ////////////////////////////////////////////////////////////////////////////////
  _clearAllInputData() {
    for (let model of inputModelReleaseOrder) {
      set(this.model.inputs, inputModels[model], null);
    }
  }

  _replaceInputErrors(inputType, newErrors) {
    for (let error of this.errors) {
      if (error.tab !== inputType) {
        //TODO: Change error.tab to error.inputType
        newErrors.push(error);
      }
    }
    set(this, 'errors', newErrors);
  }

  _selectTab(trigger, tab) {
    if (this.enabledTabs.includes(tab) && tab !== this.activeTab) {
      // Send Analytics Data from Previous Tab
      let oldTab = this.activeTab;
      if (trigger !== 'LOAD') {
        sendTabExitAnalytics(this.model, oldTab, this.analytics, this.logger);
      }

      this._spin();
      next(this, () => {
        if (!this.isDestroying && !this.isDestroyed) {
          // Clear the Errors when we move to a different tab, we will just regenerate them again once we visit the tab again
          set(this, 'errors', []);

          if (tab === 'components') {
            this.systemDialogFunc('ON', 'loading-opamp-data');
            this.opamps.getRequest().then(() => {
              this.systemDialogFunc('OFF', '');
              this._nospin();
              if (this.opamps.getOpAmps() !== null && this.opamps.getOpAmps().length > 0) {
                // Update Selected Figure on Selected Tab
                this._updateTabFigure(tab, this._updateVisitedTabs(tab));

                // Update Tab Input Model
                this._updateInputModel(tab);

                // If there is an error, find the first tab where an error occurred
                const errorTab = this.errors.length > 0 ? this.errors[0].tab : '';

                // Update Tab
                this.activeTab = errorTab !== '' ? errorTab : tab;

                // If there is an error, focus to that tab
                this._forceFocusTab(errorTab);

                // Update fromErrors, fromModel
                this.fromErrors = this._updateFromErrors(errorTab);
                this.fromModel = this._updateFromModel();
              } else {
                // OpAmp Data Unavailable notification
                this._showNotification('dialogs.system.title', 'dialogs.system.error-opamp-data');
                return;
              }
            });
          } else {
            // Update Selected Figure on Selected Tab
            this._updateTabFigure(tab, this._updateVisitedTabs(tab));

            // Update Tab Input Model
            this._updateInputModel(tab);

            // If there is an error, find the first tab where an error occurred
            const errorTab = this.errors.length > 0 ? this.errors[0].tab : '';

            // Update Tab
            this.activeTab = errorTab !== '' ? errorTab : tab;

            // If there is an error, focus to that tab
            this._forceFocusTab(errorTab);

            // Update fromErrors, fromModel
            this.fromErrors = this._updateFromErrors(errorTab);
            this.fromModel = this._updateFromModel();

            this._nospin();
          }
        }
      });

      // If Tabs is Next Steps, send Analytics Data for Parts
      if (camelize(tab) === FilterConstants.tabs.nextSteps) {
        sendPartStageDataNextStepsAnalytics(this.model, this.analytics, this.intl);
      }

      // Send Analytics that a user has Selected a Tab (except for a LOAD trigger that resets the tab back to )
      if (trigger !== 'LOAD') {
        sendTabNavigationAnalytics(oldTab, tab, trigger, this.analytics, this.logger);
      }

      // Logging
      this.logger.log('Tab = ' + tab);
    }
  }

  _updateVisitedTabs(tab) {
    let isInitialVisit = false;
    let visitedTabs = this.visitedTabs.slice();
    if (tab !== 'filter-type' && !visitedTabs.includes(tab)) {
      visitedTabs.push(tab);
      this.visitedTabs = visitedTabs;
      isInitialVisit = true;
    }
    return isInitialVisit;
  }

  _updateInputModel(inputType) {
    // If we're moving forward, restructure all *intermediate* tabs between current and new tab
    // If the current inputType isn't a tab, this part will be a no-op
    let first = tabList.indexOf(this.activeTab);
    let last = tabList.indexOf(inputType);
    if (last > first) {
      for (let i = first + 1; i < last; i++) {
        let advisor = this.advisors[tabList[i]];
        if (advisor && advisor.restructure && tabList[i] in inputModels) {
          set(
            this.model.inputs,
            inputModels[tabList[i]],
            advisor.restructure(this.model.inputs[inputModels[tabList[i]]]),
          );
        }
        if (advisor && advisor.validate) {
          let tabErrorList = advisor.validate(this.model.inputs[inputModels[tabList[i]]], []);
          this._replaceInputErrors(inputType, tabErrorList);
        }
      }
    }
    // Now deal with the current item
    let advisor = this.advisors[inputType];
    if (advisor && inputType in inputModels) {
      if (!this.model.inputs[inputModels[inputType]]) {
        // Brand new model
        set(this.model.inputs, inputModels[inputType], advisor.generate());
      } else if (advisor.restructure) {
        // Only restructure if advisor supports it
        set(this.model.inputs, inputModels[inputType], advisor.restructure(this.model.inputs[inputModels[inputType]]));
      }
    }
  }

  _updateTabFigure(tab, initialVisit) {
    // Get the current selected figure
    if (figuresOfTab[tab].length > 0) {
      // Let's generalize it, not just filter-type, but tabs without figures
      let selectedFigure = this.selectedFigure,
        figureExists =
          !!selectedFigure &&
          figuresOfTab[tab].some(function (item) {
            return item.name === selectedFigure;
          });
      this.logger.log(`initialVisit = ${initialVisit}; figureExists = ${figureExists}`);

      // If the tab is visited for the first time, automatically set the default figure for the selected tab.
      // If not, check first if the current figure exists on the new selected tab, if not, then use the default figure.
      let newSelectedFigure = this.figurePerTab[tab];
      this._spin();
      next(this, () => {
        if (!this.isDestroying && !this.isDestroyed) {
          this._updateInputModel(newSelectedFigure);
          this.selectedFigure = newSelectedFigure;
          this._nospin();
        }
      });
    }
  }

  _showNotification(title, message, msgparams) {
    let adjparams = msgparams;
    if (adjparams) {
      adjparams.htmlSafe = 'true';
    } else {
      adjparams = { htmlSafe: 'true' };
    }
    set(this.notificationDialog, 'title', this.intl.t(title, { htmlSafe: 'true' }));
    set(this.notificationDialog, 'message', this.intl.t(message, adjparams));
    set(this.notificationDialog, 'enabled', true);
  }

  _showHelp(title, message, msgparams) {
    let adjparams = msgparams;
    if (adjparams) {
      adjparams.htmlSafe = 'true';
    } else {
      adjparams = { htmlSafe: 'true' };
    }
    set(this.helpDialog, 'title', this.intl.t(title, { htmlSafe: 'true' }));
    set(this.helpDialog, 'message', this.intl.t(message, adjparams));
    set(this.helpDialog, 'enabled', true);
  }

  _downloadDesign(all) {
    let filesToUpload = [];
    let isLocalPDF = this.pdf ? this.pdf.toLowerCase() !== 'remote' : false;
    this.logger.time('downloadDesignTime');
    return RSVP.Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'download-requesting');
        this.logger.time('downloadDataBuildTime');
        return delay(100).then(() => {
          // Delay to allow Dialog Message and Spinner to Appear
          if (all) {
            this._createPDFImages();
            if (!this.htmlToCanvas) {
              return Promise.resolve(import('html2canvas'))
                .then((module) => {
                  return module.default;
                })
                .then((htmlToCanvas) => {
                  this.htmlToCanvas = htmlToCanvas;
                });
            }
          }
          return;
        });
      })
      .then(() => {
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          if (all) {
            if (!this.pdfReport && isLocalPDF) {
              return RSVP.Promise.resolve(import('@ebiz/aditools-design-report'))
                .then((module) => {
                  return module.default;
                })
                .then((module) => {
                  this.pdfReport = module;
                  return;
                })
                .then(() => {
                  return this._buildPDFFonts();
                })
                .then((pdfFonts) => {
                  this.designFiles.pdfFonts = pdfFonts;
                  return;
                });
            }
          }
          return;
        });
      })
      .then(() => {
        let savedDesignFile = [];
        if (all) {
          // 1. Add JSON Design to the Upload List
          this.systemDialogFunc('ON', 'download-create-design');
          savedDesignFile = this.designFiles.buildSavedDesign(
            this.model.inputs,
            this.visitedTabs,
            this.designName,
            this.designDesc,
            this.advisors['components'],
          );
        }
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          return savedDesignFile;
        });
      })
      .then((savedDesignFile) => {
        let csvFiles = [];
        filesToUpload = filesToUpload.concat(savedDesignFile);
        if (all) {
          // 2. Add Graph Data (which will be converted to CSV in the API)
          this.systemDialogFunc('ON', 'download-create-data');
          csvFiles = this.designFiles.buildGraphToCSVDataFiles(this.model.results);
        }
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          return csvFiles;
        });
      })
      .then((csvFiles) => {
        let evalBoardFiles = [];
        filesToUpload = filesToUpload.concat(csvFiles);
        if (all) {
          // 3. Add Eval Board Links to ZIP Files to be Added to the Upload List
          this.systemDialogFunc('ON', 'download-create-eval-board');
          evalBoardFiles = this.designFiles.buildEvalBoardFiles(
            this.model.results.nextSteps,
            this.siteconfig.AdiLocale,
          );
        }
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          return evalBoardFiles;
        });
      })
      .then((evalBoardFiles) => {
        let bomFile = [];
        filesToUpload = filesToUpload.concat(evalBoardFiles);
        if (all) {
          // 4. Add BOM Data (which will be converted to CSV in the API)
          this.systemDialogFunc('ON', 'download-create-bom');
          bomFile = this.designFiles.buildBOMData(this.model.results.nextSteps);
        }
        return delay(10).then(() => {
          return bomFile;
        });
      })
      .then((bomFile) => {
        filesToUpload = filesToUpload.concat(bomFile);
        this.systemDialogFunc('ON', 'download-create-spice');
        // Get the Macro Models for the Netlists
        let opAmpsList = this.model.inputs.components.individualStageInputs.map((item) => {
          return item.opAmp;
        });
        let opAmpsListGeneric = opAmpsList.map((item) => {
          return this.opamps.getOpAmp(item).generic.toUpperCase();
        });
        let macroModels = SpiceNetlistsUtil.getModelFiles(opAmpsListGeneric, this.cdp);
        return delay(10).then(async () => {
          // Delay to allow Dialog Message to Appear
          let modelFiles = {};
          for (let i = 0; i < opAmpsList.length; i++) {
            modelFiles[opAmpsList[i]] = await macroModels[opAmpsListGeneric[i]];
          }
          return [modelFiles, opAmpsList];
        });
      })
      .then(([modelFiles, opAmpsList]) => {
        // 5. Add the Spice Files (Netlists) to the Upload List
        let pathURL = this.siteconfig.SpiceNetlistsUrl;
        return SpiceNetlistsUtil.getSpiceNetListFiles(
          pathURL,
          this.siteconfig.MediaUrl,
          this.siteconfig.AdiLocale,
          opAmpsList,
          all ? 'all' : 'spice',
          modelFiles,
          this.model,
          this.assets,
          this.logger,
          this.fastboot,
        );
      })
      .then((netListFilesAndParts) => {
        filesToUpload = filesToUpload.concat(netListFilesAndParts.downloadFiles);
        // 6. Add the Spice Files (Schematics) to the Upload List
        //let directory = all ? FilterConstants.spiceFolderDownloadPath : '';
        let pathURL = this.siteconfig.SpiceSchematicsUrl;
        return SpiceSchematicsUtil.getSpiceSchematicFiles(
          pathURL,
          all ? 'all' : 'spice',
          this.model,
          this.assets,
          this.logger,
          this.fastboot,
          this.intl,
          netListFilesAndParts.netListParts,
        );
      })
      .then((spiceFiles) => {
        if (spiceFiles !== null && spiceFiles.length > 0) {
          filesToUpload = filesToUpload.concat(spiceFiles);
        }
        // 7. Generate Images for PDF Use
        let imagePromiseArray = [];
        if (all) {
          this.systemDialogFunc('ON', 'download-create-graph');
          let pdfFigureList = this.pdfFigures.components;
          for (let i = 0; i < pdfFigureList.length; i++) {
            // I can actually use _convertHTMLToImage for everything, but when it comes to large SVGs performance
            // seems to degrade. Using this If Block improves performance
            if (i !== 10) {
              // For the Graphs
              imagePromiseArray.push(this.snapshot.convertSVGToImage('#pdfimage-' + pdfFigureList[i].name));

              // For debugging/testing purposes, you can also use this.
              /*
              imagePromiseArray.push(
                this.snapshot.convertHTMLToImage('#pdfimage-' + pdfFigureList[i].name, this.htmlToCanvas)
              );
              */
            } else {
              // For the Stages
              imagePromiseArray.push(
                this.snapshot.convertHTMLToImage('#pdfimage-' + pdfFigureList[i].name, this.htmlToCanvas),
              );
            }
          }
        }
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          return RSVP.Promise.all(imagePromiseArray);
        });
      })
      .then((imageList) => {
        let pdfFile = [];
        if (all) {
          // 8. Add Data for PDF Creation to the Upload List
          this.systemDialogFunc('ON', 'download-create-pdf');
          if (isLocalPDF) {
            // PDF is created by Client
            pdfFile = this.designFiles.buildPDFBinaryData(
              this.model,
              imageList,
              this.designName,
              this.designDesc,
              this.pdfReport,
            );
          } else {
            // PDF is created by ToolsAPI
            pdfFile = this.designFiles.buildPDFData(this.model, imageList, this.designName, this.designDesc);
          }
        }
        return delay(10).then(() => {
          // Delay to allow Dialog Message to Appear
          return pdfFile;
        });
      })
      .then((pdfFile) => {
        filesToUpload = filesToUpload.concat(pdfFile);
        // 9. Build Download Data
        let downloadData = {
          ToolId: 1,
          ToolName: 'FW',
          UploadFiles: filesToUpload,
        };
        this.logger.timeEnd('downloadDataBuildTime');
        return downloadData;
      })
      .then((downloadData) => {
        // 10. Send Download Data to API
        this.systemDialogFunc('ON', 'download-downloading');
        this.download.downloadDesign(downloadData).then(([contentType, fileName, result]) => {
          // 11. Download Finished
          return delay(10).then(() => {
            // Delay to allow Dialog Message to Appear
            this.systemDialogFunc('OFF', '');
            set(this, 'pdfFigures', { render: false, components: [] });
            if (result !== null) {
              downloadFile(contentType, fileName, result);
            } else {
              // Error Status
              this._showNotification('download.error-title', 'download.error-message');
            }
            this.logger.timeEnd('downloadDesignTime');
          });
        });
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this._showNotification('download.error-title', 'download.error-message');
        this.logger.log('Error:' + error.toString());
        this.logger.timeEnd('downloadDesignTime');
      });
  }

  _createPDFImages() {
    let resultSource = 'nextSteps';
    let pdfFigures = FigureMap[dasherize(resultSource)];
    let imageComponents = [];
    let excludeIndex = [10, 11, 14];
    let specialIndex = [9];
    for (let i = 0; i < pdfFigures.length; i++) {
      if (!excludeIndex.includes(i)) {
        if (!specialIndex.includes(i)) {
          imageComponents.push({
            name: pdfFigures[i].name,
            figure: pdfFigures[i].figure,
            source: pdfFigures[i].source
              ? this.model.results[resultSource][pdfFigures[i].source]
              : this.model.results[resultSource],
            options: pdfFigures[i].options,
          });
        } else {
          switch (i) {
            case 9: {
              imageComponents.push({
                name: pdfFigures[i].name,
                figure: 'figures/graph',
                source: this.model.results[resultSource][pdfFigures[i].source].graphData,
                options: pdfFigures[i].options,
              });
              break;
            }
            default:
              break;
          }
        }
      }
    }
    set(this, 'pdfFigures', { render: true, components: imageComponents });
  }

  _updateModelNumber(evalBoardData) {
    let numOfStages = evalBoardData.length;
    let workingEvalBoardData = [];

    for (var a = 0; a < numOfStages; a++) {
      var evalBoardItem = Object.assign({}, evalBoardData[a]);
      workingEvalBoardData.push(evalBoardItem);
    }

    // all daughter boards (except for motherboard) will be shipped as 4-packs.
    // So if the design calls for multiple evaluation boards of 1 type, as long as 4 or less are needed, customer will only need to order one 4-pack.
    set(this, 'evalBoardMultiple', false);

    for (var i = 1; i < numOfStages; i++) {
      if (workingEvalBoardData[i].evalBoardModelNumber !== null) {
        if (workingEvalBoardData[i].evalBoardModelNumber.indexOf('*') === -1) {
          var evalBoardName = workingEvalBoardData[i].evalBoardModelNumber;
          var j = i + 1;
          var count = 1;
          for (j; j < numOfStages; j++) {
            if (evalBoardName === workingEvalBoardData[j].evalBoardModelNumber) {
              count++;
              if (count !== 5) {
                workingEvalBoardData[j].evalBoardModelNumber = '*' + workingEvalBoardData[j].evalBoardModelNumber;
                set(this, 'evalBoardMultiple', true);
              }
            }
          }
        }
      }
    }

    // if there is more than 4 stages, we need more than one motherboard
    // each motherboard can hold up to 4 daughterboards.
    const numOfStagesActual = evalBoardData.filter((x) => x.stage !== '').length;
    if (numOfStagesActual > 4) {
      workingEvalBoardData[0].evalBoardModelNumber = '2 x ' + workingEvalBoardData[0].evalBoardModelNumber;
    }

    set(this, 'evalBoardDisplayData', workingEvalBoardData);
  }

  // not needed, already defined in the component AditoolsDialogSamples
  // _buildOrderSamplesShoppingUrl(checkedSamples) {
  //   let checkedClause = checkedSamples.length > 0 ? '&modelNbr=' + encodeURIComponent(checkedSamples.join(',')) : '';
  //   // let details = `&sc=true&ACTION=SAMPLES_BUNDLES${checkedClause}`;
  //   return checkedClause;
  // }

  _spin() {
    set(this, 'updating', true);
  }

  _nospin() {
    set(this, 'updating', false);
  }

  _updateFromErrors(inputType) {
    let tabList = this.tabs,
      result = [];
    for (let i = 0; i < tabList.length; i++) {
      result.push(tabList[i]);
      if (tabList[i] === inputType) break;
    }
    return result;
  }

  _updateFromModel() {
    let tabList = this.tabs,
      result = [];
    for (let i = 0; i < tabList.length; i++) {
      let advisor = this.advisors[tabList[i]];
      result.push(tabList[i]);
      if (typeof advisor !== 'undefined' && advisor.hasRestrictions()) break;
    }
    return result;
  }

  _getDimensionsOnResize() {
    let _width = window.innerWidth;
    let _height = window.innerHeight;
    this.width = _width < 0 ? 0 : _width;
    set(this, 'height', _height < 0 ? 0 : _height);
  }

  _updateToolbarCss() {
    if (this.siteconfig.AdiLocale === 'jp') {
      let sheets = document.styleSheets;
      for (let i = 0; i < sheets.length; i++) {
        let csshref = sheets[i].href;
        if (csshref && csshref.includes('filter-wizard.css')) {
          let rules = sheets[i].cssRules;
          for (let j = 0; j < rules.length; j++) {
            let rule = rules[j];
            if (rule.style) {
              let selectorText = rule.selectorText;
              if (selectorText.toLowerCase() === '.aditools-menubar .menu-links > .menu-link') {
                rule.style.fontSize = '12px';
                break;
              }
            }
          }
        }
      }
    }
  }

  _updateFigurePerTab() {
    let figurePerTab = this.figurePerTab,
      currentFigure = this.selectedFigure;

    for (let _tab in figuresOfTab) {
      if (figurePerTab[_tab].length > 0) {
        let existsInFigures = figuresOfTab[_tab].some((item) => {
          return item.name === currentFigure;
        });
        //this.logger.log(_tab, this.visitedTabs.includes(_tab) ? ' tab visited.' : 'tab not yet visited.');
        if (existsInFigures) {
          if (_tab === 'components' && !this.visitedTabs.includes(_tab)) {
            let _figure = figurePerTab[_tab];
            figurePerTab[_tab] = _figure;
          } else {
            figurePerTab[_tab] = currentFigure;
          }
        } else {
          let _figure = figurePerTab[_tab];
          figurePerTab[_tab] = _figure;
        }
      }
    }
    set(this, 'figurePerTab', Object.assign({}, figurePerTab));
  }

  _buildPDFFonts() {
    let fontURL = '';
    let fontName = '';
    let fontFileName = '';
    let pdfFonts = [];
    this.systemDialogFunc('ON', 'application-busy');
    switch (this.siteconfig.AdiLocale) {
      case 'en':
      case 'ru':
        fontName = 'Arial';
        fontFileName = 'arial.ttf';
        fontURL = getURL(ENV.rootURL + FilterConstants.enFontResource, this.fastboot, this.logger);
        break;
      case 'jp':
        fontName = 'NotoSansJP';
        fontFileName = 'NotoSansJP-Regular.ttf';
        fontURL = getURL(ENV.rootURL + FilterConstants.jpFontResource, this.fastboot, this.logger);
        break;
      case 'cn':
        fontName = 'NotoSansSC';
        fontFileName = 'NotoSansSC-Regular.ttf';
        fontURL = getURL(ENV.rootURL + FilterConstants.cnFontResource, this.fastboot, this.logger);
        break;
    }
    this.logger.time(`Download ${fontName} font`);
    return this.assets.getAsset(fontURL, 'BLOB').then((content) => {
      return new RSVP.Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.readAsDataURL(content);
        reader.onloadend = () => {
          let fontData = reader.result;
          let fontIndex = fontData.indexOf(';base64,');
          if (fontIndex > -1) {
            fontData = fontData.substring(fontIndex + ';base64,'.length);
          }
          this.logger.timeEnd(`Download ${fontName} font`);
          if (fontData !== null) {
            pdfFonts.push({ fileName: fontFileName, fontName: fontName, fontStyle: 'normal', data: atob(fontData) });
            resolve(pdfFonts);
          } else {
            reject(pdfFonts);
          }
        };
      });
    });
  }

  _sendDesign() {
    return RSVP.Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'sending-filter', { tool: this.toolNameDisplay });
        return delay(200).then(() => {
          return;
        });
      })
      .then(() => {
        let designData = this.persistence.dry(
          this.visitedTabs,
          this.model.inputs,
          this.model.results.specifications,
          this.advisors['components'],
        );
        set(this, 'designSendTimeOut', { timeOut: null, handlerFunc: this.designSendTimeOutError });
        return this.channel.sendDesign(designData, this.designSendTimeOut);
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        set(this.systemLock, 'enabled', false);
        this._showNotification('dialogs.send-filter.title-error', 'dialogs.send-filter.message-error', {
          tool: this.toolNameDisplay,
        });
        this.logger.log('Error:' + error.toString());
      });
  }

  _forceFocusTab(tab) {
    if (tab !== '') {
      const tabElem = document.querySelector(`[data-qa-element="menu_bar_${tab.replace('-', '_')}_tab"]`);
      tabElem.focus();
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Controller Actions                                                         //
  ////////////////////////////////////////////////////////////////////////////////

  @action updateModel(inputType, modeldata) {
    // On filter type change, consider filter type tab visited and forget you ever visited any other pages
    if (inputType === 'filter-type' && this.model.inputs[inputModels[inputType]] !== modeldata) {
      this.visitedTabs = ['filter-type'];
      set(this, 'figurePerTab', Object.assign({}, defaultFigures));
      this._clearAllInputData();
    }
    this._replaceInputErrors(inputType, []);
    this._spin();
    next(this, () => {
      if (!this.isDestroying && !this.isDestroyed) {
        set(this.model.inputs, inputModels[inputType], modeldata);
        if (inputType === 'filter-type') {
          this._selectTab('TAB', 'specifications');
        }
        this.fromErrors = this._updateFromErrors('');
        this.fromModel = this._updateFromModel();
        this._nospin();
      }
    });
    this.logger.log(`Update (${inputType})`, modeldata);
  }

  @action updateErrors(inputType, errors) {
    this._replaceInputErrors(inputType, errors.slice());
    this.fromErrors = this._updateFromErrors(inputType);
  }

  @action selectTab(trigger, tab) {
    this._selectTab(trigger, tab);
  }

  @action selectFigure(figure) {
    this._spin();
    later(
      this,
      () => {
        if (!this.isDestroying && !this.isDestroyed) {
          this._updateInputModel(figure.name);
          this.selectedFigure = figure.name;
          this._updateFigurePerTab();
          this._nospin();
        }
      },
      100,
    );
  }

  @action switchLanguage(adilang) {
    let oldLocale = this.intl.locale;
    oldLocale = oldLocale === 'ja' ? 'jp' : oldLocale === 'zh' ? 'cn' : oldLocale;
    if (location.href.indexOf('designtools') !== -1) {
      location.href = `/designtools/${adilang}/filterwizard/`;
    } else {
      location.href = `?lang=${adilang}`;
    }
    sendLocaleChangeAnalytics(oldLocale, adilang, this.analytics, this.logger);
    this.logger.log('Locale = ' + adilang);
  }

  @action takeStep(requestedAction) {
    switch (requestedAction) {
      case 'order': {
        RSVP.Promise.resolve()
          .then(() => {
            this.systemDialogFunc('ON', 'application-busy');
            return delay(10).then(() => {
              return this.model.results.nextSteps.orderSamplesData;
            });
          })
          .then((orderSamples) => {
            this.systemDialogFunc('OFF', '');
            set(this, 'orderSamplesData', orderSamples);
            set(this.dialogFlags, 'orderSamplesEnabled', true);
            sendOpenCloseDialogAnalytics('Order samples', 'O', this.model, this.analytics, this.intl);
            this.logger.log('Next Step (' + requestedAction + ')');
          });
        break;
      }
      case 'evalboard':
        RSVP.Promise.resolve()
          .then(() => {
            this.systemDialogFunc('ON', 'application-busy');
            return delay(10).then(() => {
              this._updateModelNumber(this.model.results.nextSteps.evalBoardData);
              return;
            });
          })
          .then(() => {
            this.systemDialogFunc('OFF', '');
            const inductor = this.evalBoardDisplayData.find(
              (x) => x.stageImplementation === 'inductor' && CommonUtils.isNullOrUndefined(x.evalBoardModelNumber),
            );
            if (inductor) {
              set(this.dialogFlags, 'evalBoardConfirmationEnabled', true);
            } else {
              set(this.dialogFlags, 'evalBoardEnabled', true);
              sendOpenCloseDialogAnalytics('Order eval board', 'O', this.model, this.analytics, this.intl);
              this.logger.log('Next Step (' + requestedAction + ')');
            }
          });
        break;
      case 'share':
        sendOpenCloseDialogAnalytics('Mail', 'O', this.model, this.analytics, this.intl);
        set(this.dialogFlags, 'mailToColleagueEnabled', true);
        this.logger.log('Next Step (' + requestedAction + ')');
        break;
      case 'downloadAll':
        downloadDesignAnalytics('Get all files', this.model, this.analytics, this.intl);
        this._downloadDesign(true);
        this.logger.log('Next Step (' + requestedAction + ')');
        break;
      case 'downloadSpice':
        downloadDesignAnalytics('Get spice only', this.model, this.analytics, this.intl);
        this._downloadDesign(false);
        this.logger.log('Next Step (' + requestedAction + ')');
        break;
      case 'sendDesign':
        this._sendDesign();
        this.logger.log('Next Step (' + requestedAction + ')');
        break;
      default:
        break;
    }
  }

  @action closeDialog(type) {
    switch (type) {
      case 'order':
        sendOpenCloseDialogAnalytics('Order samples', 'C', this.model, this.analytics, this.intl);
        set(this.dialogFlags, 'orderSamplesEnabled', false);
        break;
      case 'evalboard':
        sendOpenCloseDialogAnalytics('Order eval board', 'C', this.model, this.analytics, this.intl);
        set(this.dialogFlags, 'evalBoardEnabled', false);
        break;
      case 'share':
        sendOpenCloseDialogAnalytics('Mail', 'C', this.model, this.analytics, this.intl);
        set(this.dialogFlags, 'mailToColleagueEnabled', false);
        break;
      case 'feedback':
        sendOpenCloseDialogAnalytics('Feedback', 'C', this.model, this.analytics, this.intl);
        break;
      case 'look-evalboard':
        sendOpenCloseDialogAnalytics('Look order eval board', 'C', this.model, this.analytics, this.intl);
        set(this.dialogFlags, 'evalBoardConfirmationEnabled', false);
        break;
      case 'algorithm':
        if (
          this.model.inputs.components.preferredAlgorithms === undefined ||
          this.model.inputs.components.preferredAlgorithms === null
        ) {
          this.model.inputs.components.preferredAlgorithms = FilterConstants.model.inputs.components.oldAlgorithm;
        }
        set(this.dialogFlags, 'algorithmEnabled', false);
        break;
      default:
        break;
    }
  }

  @action confirmLookEvalBoard() {
    set(this.dialogFlags, 'evalBoardEnabled', true);
    sendOpenCloseDialogAnalytics('Order eval board', 'O', this.model, this.analytics, this.intl);
    this.logger.log('Next Step (evalboard)');
  }

  @action showNotification(title, message, msgparams) {
    this._showNotification(title, message, msgparams);
  }

  @action closeNotification() {
    set(this.notificationDialog, 'enabled', false);
    set(this.notificationDialog, 'title', '');
    set(this.notificationDialog, 'message', '');
  }

  @action closeEdgeWarning() {
    set(this.edgeWarning, 'enabled', false);
  }

  @action showHelp(title, message, msgparams) {
    this._showHelp(title, message, msgparams);
  }

  @action closeHelp() {
    set(this.helpDialog, 'enabled', false);
    set(this.helpDialog, 'title', '');
    set(this.helpDialog, 'message', '');
  }

  @action systemDialogFunc(status, message, msgparams) {
    switch (status) {
      case 'ON':
        set(this.systemDialog, 'enabled', true);
        set(this.systemDialog, 'message', this.intl.t(`dialogs.system.${message}`, msgparams));
        break;
      case 'OFF': {
        const dialogBody = document.querySelector('.aditools-system-dialog');
        if (dialogBody && dialogBody.parentElement) {
          dialogBody.parentElement.close();
        }
        set(this.systemDialog, 'enabled', false);
        set(this.systemDialog, 'message', '');
        break;
      }
      case 'NONE':
        break;
      default:
        break;
    }
  }

  @action loadDesign(jsonStringData) {
    set(this, 'updating', true);
    this.systemDialogFunc('ON', 'application-busy');
    this.opamps.getRequest().then(() => {
      set(this, 'updating', false);
      if (!this.isDestroying && !this.isDestroyed) {
        let inputs = this.persistence.load(jsonStringData);
        switch (inputs.errorCode.code) {
          case FilterConstants.loadErrorCodes.noError: {
            this._selectTab('LOAD', 'filter-type');
            next(this, () => {
              if (!this.isDestroying && !this.isDestroyed) {
                const hasOldAlgorithm = this.persistence.hasOldAlgorithm(jsonStringData, inputs.inputModel);
                this.visitedTabs = inputs.visitedTabs;
                this.model.inputs.filterType = inputs.inputModel.filterType;
                this.model.inputs.specifications = inputs.inputModel.specifications;
                this.model.inputs.components = inputs.inputModel.components;
                this.model.inputs.tolerances = inputs.inputModel.tolerances;
                this.model.inputs.nextSteps = inputs.inputModel.nextSteps;
                this.logger.log('Load submitted. \nDesign Data: ' + jsonStringData);
                this.systemDialogFunc('OFF', '');
                if (hasOldAlgorithm) {
                  set(this.dialogFlags, 'algorithmEnabled', true);
                }
              }
            });
            break;
          }
          case FilterConstants.loadErrorCodes.generalError: {
            this.systemDialogFunc('OFF', '');
            this._showNotification('dialogs.load.title', 'dialogs.load.invalid-file');
            break;
          }
          case FilterConstants.loadErrorCodes.invalidPartFound: {
            this.systemDialogFunc('OFF', '');
            this._showNotification('dialogs.load.title', 'dialogs.load.invalid-parts', {
              numParts: inputs.errorCode.details.split(', ').length,
              parts: inputs.errorCode.details,
            });
            break;
          }
          case FilterConstants.loadErrorCodes.noOpAmps: {
            this.systemDialogFunc('OFF', '');
            this._showNotification('dialogs.load.title', 'dialogs.load.no-opamps');
            break;
          }
        }
      }
    });
  }

  @action saveDesign(name, desc) {
    set(this, 'designName', name);
    set(this, 'designDesc', desc);
    /*
    let stages = this.visitedTabs.includes(FilterConstants.tabs.components)
      ? this.model.results.components.detailsOfStages
      : null;
      */
    this.persistence.save(name, desc, this.visitedTabs, this.model.inputs, this.advisors['components']);
    this.logger.log('Save submitted. \nDesign Name: ' + name + '\nDesign Description: ' + desc);
  }

  @action shareDesign(name, sender, recipients) {
    let saveData = this.persistence.getSaveData(
      this.visitedTabs,
      this.model.inputs,
      this.advisors['components'],
    ).design;
    set(this.dialogFlags, 'mailToColleagueEnabled', false);
    return RSVP.Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'application-busy');
        return delay(250).then(() => {
          return;
        });
      })
      .then(() => {
        return this.share.shareDesign(name, sender, recipients, saveData);
      })
      .then((result) => {
        this.systemDialogFunc('OFF', '');
        if (result.status === 0) {
          shareDesignAnalytics(name, sender, recipients, saveData, this.analytics, this.logger);
          sendOpenCloseDialogAnalytics('Mail', 'S', this.model, this.analytics, this.intl);
          this._showNotification('dialogs.share.title', 'dialogs.share.share-dialog-ok-message');
        } else {
          this._showNotification('dialogs.share.title', 'dialogs.share.share-dialog-ng-message');
        }
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this._showNotification('dialogs.share.title', 'dialogs.share.share-dialog-ng-message');
        this.logger.log('Error:' + error.toString());
      });
  }

  @action processOrderSamples() {
    sendOpenCloseDialogAnalytics('Order samples', 'S', this.model, this.analytics, this.intl);
  }

  @action processEvalBoard() {
    sendOpenCloseDialogAnalytics('Order eval board', 'S', this.model, this.analytics, this.intl);
  }

  @action getAlgorithm(useV2) {
    this.model.inputs.components.preferredAlgorithms = useV2
      ? FilterConstants.model.inputs.components.defaultAlgorithm
      : FilterConstants.model.inputs.components.oldAlgorithm;
  }

  @action giveFeedback(model) {
    return RSVP.Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'application-busy');
        return delay(250).then(() => {
          return;
        });
      })
      .then(() => {
        return this.feedback.deliverFeedback(model.name, model.email, model.feedback);
      })
      .then((result) => {
        this.systemDialogFunc('OFF', '');
        if (result.status === 0) {
          this._showNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ok-message');
        } else {
          this._showNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ng-message');
        }
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this._showNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ng-message');
        this.logger.log('Error:' + error.toString());
      });
  }

  @action insertingToolbar(element) {
    this.navElement = element;
    this._updateToolbarCss();
    this._getDimensionsOnResize();
    this.boundResizeListener = this._getDimensionsOnResize.bind(this);
    window.addEventListener('resize', this.boundResizeListener);
  }

  @action destroyingToolbar() {
    this.navElement = null;
    window.removeEventListener('resize', this.boundResizeListener);
  }

  @action loadFromUrlDesign() {
    if (this.design) {
      let jsonStringData = this.persistence.soak(this.design);
      this.loadDesign(jsonStringData);
    }
  }

  @action handshakeAcknowledgedEvent(data) {
    clearTimeout(this.handShakeTimeOut.timeOut);
    console.timeEnd('Handshake');
    if (!this.fastboot.isFastBoot && data.filterWizardDesign && data.filterWizardDesign.length > 0) {
      this.loadDesign(this.persistence.soak(data.filterWizardDesign));
      this.logger.log('Loaded Design from Invoker Tool: ' + data.filterWizardDesign);
    } else {
      this.systemDialogFunc('OFF', '');
    }
    this.logger.log('Handshake has been acknowledged');
  }

  @action handShakeTimeOutError() {
    console.timeEnd('Handshake');
    this.systemDialogFunc('OFF', '');
    set(this.systemLock, 'enabled', true);
    set(this.systemLock, 'title', this.intl.t('dialogs.connect-to.title-error'));
    set(this.systemLock, 'message', this.intl.t('dialogs.connect-to.message-error', { tool: this.toolNameDisplay }));
    this.logger.log('Error: Handshake acknowledge timeout error.');
    this.channel.disconnect();
  }

  @action designAcknowledgedEvent(recipientId) {
    clearTimeout(this.designSendTimeOut.timeOut);
    console.timeEnd('Send Design');
    this.systemDialogFunc('OFF', '');
    set(this.systemLock, 'enabled', true);
    set(this.systemLock, 'title', this.intl.t('dialogs.send-filter.title-ok', { tool: this.toolNameDisplay }));
    set(this.systemLock, 'message', this.intl.t('dialogs.send-filter.message'));
    this.logger.log('Design has been received. Receipient ID: ' + recipientId);
    this.channel.disconnect();
  }

  @action designSendTimeOutError() {
    console.timeEnd('Send Design');
    this.systemDialogFunc('OFF', '');
    set(this.systemLock, 'enabled', false);
    this._showNotification('dialogs.send-filter.title-error', 'dialogs.send-filter.message-error', {
      tool: this.toolNameDisplay,
    });
    this.logger.log('Error: Design acknowledge timeout error.');
  }

  @action integrationCancelledEvent() {
    this.systemDialogFunc('OFF', '');
    set(this.systemLock, 'enabled', true);
    set(this.systemLock, 'title', this.intl.t('dialogs.send-filter.title-cancel', { tool: this.toolNameDisplay }));
    set(this.systemLock, 'message', this.intl.t('dialogs.send-filter.message'));
    this.logger.log('Integration has been cancelled from Invoker Tool');
    this.channel.disconnect();
  }

  @action onConnectHandler() {
    this.systemDialogFunc('OFF', '');
    this.logger.log('Connection has been established');
  }

  @action onDisconnectHandler() {
    this.channel.selfLink.destroy();
    this.channel.setInvokerId(null);
    this.logger.log('Disconnected');
  }

  @action onDataHandler(data) {
    switch (data.type) {
      case 'acknowledgeHandshake':
        this.handshakeAcknowledgedEvent(data);
        break;
      case 'cancelIntegration':
        this.integrationCancelledEvent();
        break;
      case 'design':
        if (!this.fastboot.isFastBoot && data.filterWizardDesign && data.filterWizardDesign.length > 0) {
          this.loadDesign(this.persistence.soak(data.filterWizardDesign));
          this.logger.log('Loaded Design from Invoker Tool: ' + data.filterWizardDesign);
        } else {
          this.systemDialogFunc('OFF', '');
        }
        break;
      case 'acknowledgeDesign':
        this.designAcknowledgedEvent(this.channel.invokerId);
        break;
      default:
        break;
    }
  }
  @action toolbarMenuClick(menuLink) {
    const tab = this.intl.t('wizard.' + this.activeTab, { locale: 'en-us' });
    toolbarLinkClickAnalytics(menuLink, tab, this.analytics);
  }
}
